import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { AccountingType } from 'v2-types/order';
import { useAccountingUpdateSocketEvent } from 'hooks/useAccountingUpdateFromSocket';
import { getOrderForSettlement } from './services/querys';

type PartyRole = {
  name: string;
  kinds: string[];
  entities: string[];
};

const NET_PROCEEDS_CODES = ['netproceeds_buyer', 'netproceeds_seller'];

const useSettlement = () => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const [settlementAccounting, setSettlementAccounting] = useState<AccountingType[]>([]);
  const [allAccounting, setAllAccounting] = useState<AccountingType[]>([]);
  const [partyRoles, setPartyRoles] = useState<Record<string, PartyRole>>({});
  const [viewMode, setViewMode] = useState<'issues' | 'balanced' | 'all'>('issues');
  const queryCache = useQueryClient();
  const reloadAmounts = () => queryCache.invalidateQueries(['settlement-order-accounting', orderId]);

  // Helper: Get effective amount for an entry
  const getEffectiveAmount = useCallback((entry: AccountingType): number => {
    if (entry.amount_override !== 0) return entry.amount_override;
    if (entry.amount_calculated !== 0) return entry.amount_calculated;
    return entry.amount;
  }, []);

  // Helper: Check if entry has valid identifiers (letter and number)
  const hasValidIdentifiers = useCallback((entry: AccountingType): boolean => {
    // Letter must be a string and non-empty after trim
    const hasValidLetter = typeof entry.letter === 'string' && entry.letter.trim() !== '';
    // Number must not be null or undefined
    const hasValidNumber = entry.number != null;
    return hasValidLetter && hasValidNumber;
  }, []);

  // Helper: Check if entry is balanced (all 4 conditions must hold)
  // Must match SettlementTable filtering (p.amount > 0) or header/table counts diverge
  const isBalanced = useCallback((entry: AccountingType): boolean => {
    const payers = entry.involved_parties?.filter((p: any) => p.kind === 'Payer' && (p.amount || 0) > 0) || [];
    const payees = entry.involved_parties?.filter((p: any) => p.kind === 'Payee' && (p.amount || 0) > 0) || [];

    // Condition 1: Both payer and payee groups must exist (non-empty)
    if (payers.length === 0 || payees.length === 0) {
      return false;
    }

    const payersAmount = payers.reduce((sum: number, p: any) => sum + (p.accounted_amount || 0), 0);
    const payeesAmount = payees.reduce((sum: number, p: any) => sum + (p.accounted_amount || 0), 0);
    const effectiveAmount = getEffectiveAmount(entry);

    // Condition 2: Payers and payees amounts must be within $0.01 tolerance
    if (Math.abs(payersAmount - payeesAmount) > 0.01) {
      return false;
    }

    // Condition 3: Payers amount must match effective amount within $0.01 tolerance
    if (Math.abs(payersAmount - effectiveAmount) > 0.01) {
      return false;
    }

    // Condition 4: Payees amount must match effective amount within $0.01 tolerance
    if (Math.abs(payeesAmount - effectiveAmount) > 0.01) {
      return false;
    }

    return true;
  }, [getEffectiveAmount]);

  useAccountingUpdateSocketEvent({
    callback: () => reloadAmounts(),
  });

  const getOrderAccounting = useQuery(
    ['settlement-order-accounting', orderId],
    () => getOrderForSettlement(orderId),
    {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data && data.getOrder.accounting) {
          // Store ALL accounting entries for balancing tables
          setAllAccounting(data.getOrder.accounting);

          // Parse parties data: create mapping of party name to roles and entities
          const partyMap: Record<string, PartyRole> = {};
          if (data.getOrder.parties) {
            data.getOrder.parties.forEach((party: any) => {
              partyMap[party.name] = {
                name: party.name,
                kinds: party.kinds || [],
                entities: (party.member_of || []).map((m: any) => m.name).filter(Boolean),
              };
            });
          }
          setPartyRoles(partyMap);

          // Filter for settlement table: only entries with valid identifiers, amount > 0, and Payer/Payee (net proceeds entries are included by code)
          const filtered = data.getOrder.accounting
            .filter((entry: AccountingType) => {
              // Net proceeds codes are included before any other check, including the letter J exclusion
              if (NET_PROCEEDS_CODES.includes(entry.code)) return true;
              // Exclude letter J entirely (fee subtotals, not settlement entries)
              if (typeof entry.letter === 'string' && entry.letter.trim().toUpperCase() === 'J') return false;
              // Must have valid identifiers (letter and number)
              if (!hasValidIdentifiers(entry)) return false;
              // Must have positive effective amount
              const amount = getEffectiveAmount(entry);
              if (amount <= 0) return false;
              // Must have at least one Payer or Payee with amount > 0 (matches SettlementTable render filter)
              return entry.involved_parties?.some(
                (party: any) => (party.kind === 'Payer' || party.kind === 'Payee') && (party.amount || 0) > 0
              );
            })
            .sort((a: AccountingType, b: AccountingType) => {
              const letterA = (a.letter || '').padEnd(3, ' ');
              const letterB = (b.letter || '').padEnd(3, ' ');
              if (letterA !== letterB) return letterA.localeCompare(letterB);
              const numA = a.number != null ? parseInt(String(a.number), 10) : Infinity;
              const numB = b.number != null ? parseInt(String(b.number), 10) : Infinity;
              if (numA !== numB) return numA - numB;
              return (a.description || '').localeCompare(b.description || '');
            });
          setSettlementAccounting(filtered);
        }
      },
    }
  );

  // Helper to check if an entry has balance issues (inverse of isBalanced)
  const hasBalanceIssue = useCallback((entry: AccountingType): boolean => {
    return !isBalanced(entry);
  }, [isBalanced]);

  // Helper to get party details (roles and entities)
  const getPartyDetails = (partyName: string): PartyRole | null => {
    return partyRoles[partyName] || null;
  };

  // Calculate issues count from all settlement entries
  const issuesCount = useMemo(
    () => settlementAccounting.filter(hasBalanceIssue).length,
    [settlementAccounting, hasBalanceIssue]
  );
  const balancedCount = useMemo(
    () => settlementAccounting.length - issuesCount,
    [settlementAccounting.length, issuesCount]
  );

  // Filter based on view mode for table display
  const displayedAccounting = useMemo(() => {
    if (viewMode === 'issues') {
      return settlementAccounting.filter(hasBalanceIssue);
    }
    if (viewMode === 'balanced') {
      return settlementAccounting.filter(entry => !hasBalanceIssue(entry));
    }
    return settlementAccounting;
  }, [viewMode, settlementAccounting, hasBalanceIssue]);

  return {
    displayedAccounting,
    settlementAccounting,
    allAccounting,
    t,
    isLoading: getOrderAccounting.isLoading,
    orderId,
    viewMode,
    setViewMode,
    issuesCount,
    balancedCount,
    getPartyDetails,
  };
};

export default useSettlement;
